import Layout from 'components/layout';

import { i18nFunct, useSiteMetadata } from '@lucid/gatsby-theme-lucid';
import { navigate } from '@reach/router';
import { graphql } from 'gatsby';
import debounce from 'lodash.debounce';
import kebabcase from 'lodash.kebabcase';
import startcase from 'lodash.startcase';
import {
  Breadcrumb,
  Button,
  Heading,
  Icon,
  IconType
} from 'lucid-components-react';
import * as queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import Select, { ValueType } from 'react-select';

import FooterAbTest from 'components/ab-tests/footer-swap/footer-swap';
import NavbarAbTest from 'components/ab-tests/nav-swap/nav-swap';
import Card, { cardTheme } from 'components/card/card';
import Grid, { gridGutter, gridLayout } from 'components/grid/grid';
import Link from 'components/link/link';
import Paginator from 'components/paginator/paginator';
import {
  Drupal,
  Drupal_EntityQueryResult,
  Drupal_Node,
  Drupal_NodeCaseStudy,
  Drupal_NodeDiscovery,
  Drupal_NodeEbook,
  Drupal_NodeProductTemplate,
  Drupal_NodeResourceCenter,
  Drupal_NodeVideoLandingPage,
  Drupal_NodeWebinar,
  Drupal_ParagraphTemplateImage,
  Drupal_TaxonomyTerm,
  Drupal_TaxonomyTermResourceCenterPersonas,
  FileConnection
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import { useMenuDefaultFooter, useMenuMainNavLinks } from 'hooks/use-menu';
import { TemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getProcessedValue } from 'utils/helpers';
import './resource-center.scss';

interface ResourceCenterDrupal extends Drupal {
  topics: Drupal_EntityQueryResult;
  personas: Drupal_EntityQueryResult;
}

interface ResourceCenterTaxonomyTerm extends Drupal_TaxonomyTerm {
  children: {
    [tid: string]: ResourceCenterTaxonomyTerm;
  };
}

interface ResourceCenterTopics {
  [tid: string]: ResourceCenterTaxonomyTerm;
}

interface OptionType {
  label: string;
  value: number;
}

interface BlogPost {
  node: {
    nid: string;
    title: string;
    path: string;
    created: string;
    field_persona: string;
    field_topic: string;
    field_homepage_summary: string;
    field_featured_image_url: string;
  };
}

interface FormattedBlogPost {
  entityBundle: string;
  entityId: string;
  entityUrl: {
    path: string;
  };
  created: string;
  title: string;
  topicIds: string[];
  personaIds: string[];
  image: string;
  summary: string;
}

interface ResourceCenterProps extends TemplateProps {
  data: {
    drupal: Drupal;
    i18nData?: FileConnection;
    allBlogPosts: {
      edges: BlogPost[];
    };
  };
}

const ResourceCenter = ({
  pageContext,
  data,
  location
}: ResourceCenterProps) => {
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;
  const { productUrl } = useSiteMetadata();

  const { topic: queryTopicName, search: querySearch } = queryString.parse(
    location.search
  );
  const i18n = i18nFunct(data.i18nData, i18nKey);

  const collapseTaxonomy = (
    curObj: ResourceCenterTopics,
    term: ResourceCenterTaxonomyTerm
  ) => {
    const { parent, tid } = term;
    const parentTid = parent![0]!.targetId;
    const isRootTopic = parentTid === 0;
    const container = isRootTopic ? curObj : curObj[parentTid!].children;
    container[tid!] = term;
    term.children = {};

    return curObj;
  };

  const drupal = data.drupal as ResourceCenterDrupal;

  const isFirstRun = useRef(true);
  const rawTopics = drupal.topics.entities as ResourceCenterTaxonomyTerm[];
  const topicRefs = {} as any;
  rawTopics
    .map(rawTopic => rawTopic.tid)
    .forEach(
      // eslint-disable-next-line react-hooks/rules-of-hooks
      tid => (topicRefs[tid!] = useRef((null as unknown) as HTMLElement))
    );
  const topics = rawTopics.reduce(collapseTaxonomy, {} as ResourceCenterTopics);
  const topicNameToTid = new Map();
  Object.keys(topics).forEach(tid => {
    const safeName = kebabcase(topics[tid].name!);
    topicNameToTid.set(safeName, tid);
  });

  const rawPersonas = drupal.personas.entities as ResourceCenterTaxonomyTerm[];
  const personas = rawPersonas.reduce(
    collapseTaxonomy,
    {} as ResourceCenterTopics
  );
  const personaNameToTid = new Map();
  Object.keys(personas).forEach(tid => {
    const safeName = kebabcase(personas[tid].name!);
    personaNameToTid.set(safeName, tid);
  });

  const formatBlogPosts = (posts: BlogPost[]) => {
    return posts.reduce((formattedPosts, post) => {
      const { node: postNode } = post;
      const personaIds = postNode.field_persona.split(',').map(persona => {
        return personaNameToTid.get(kebabcase(persona));
      });
      const topicIds = postNode.field_topic.split(',').map(topic => {
        return topicNameToTid.get(kebabcase(topic));
      });

      formattedPosts.push({
        entityBundle: 'blog_post',
        entityId: `blog-${postNode.nid}`,
        entityUrl: {
          path: postNode.path
        },
        created: postNode.created,
        personaIds,
        topicIds,
        image: postNode.field_featured_image_url,
        summary: postNode.field_homepage_summary,
        title: postNode.title
      });
      return formattedPosts;
    }, [] as FormattedBlogPost[]);
  };

  const node = drupal.nodeById as Drupal_NodeResourceCenter;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const drupalEntities = drupal.nodeQuery!.entities!.map(
    drupalEntity => drupalEntity!.entityTranslation
  ) as Drupal_Node[];
  const blogPosts = formatBlogPosts(data.allBlogPosts.edges);
  const allEntities = [...drupalEntities, ...blogPosts] as Array<
    Drupal_Node | FormattedBlogPost
  >;
  const sortedEntities = allEntities.sort((a, b): any => {
    return b.created - a.created;
  });

  const [chosenTopic, setChosenTopic] = useState(
    topicNameToTid.get(kebabcase(queryTopicName as string)) || ''
  );

  const [chosenSubtopic, setChosenSubtopic] = useState('');
  const [search, setSearch] = useState((querySearch as string) || '');
  const filterRef = useRef((null as unknown) as HTMLElement);
  const inputRef = useRef((null as unknown) as HTMLInputElement);
  const activePersona = node.fieldRcPersona
    ? node.fieldRcPersona.targetId
    : null;

  const filterByTids = (tids: string[]) => (
    entity: Drupal_Node | FormattedBlogPost
  ) => {
    if (entity && entity.entityBundle === 'discovery') {
      const {
        fieldDiscoveryPersona,
        fieldDiscoveryTopics
      } = entity as Drupal_NodeDiscovery;

      const nodeTids = [
        ...fieldDiscoveryPersona!.map(term => String(term!.targetId)),
        ...fieldDiscoveryTopics!.map(term => String(term!.targetId))
      ];

      return nodeTids.some(tid => tids.includes(tid));
    } else if (entity && entity.entityBundle === 'blog_post') {
      const { topicIds, personaIds } = entity as FormattedBlogPost;
      const nodeIds = [...topicIds, ...personaIds];
      return nodeIds.some(tid => tids.includes(tid));
    } else {
      const { fieldTopic, fieldPersona } = entity as
        | Drupal_NodeEbook
        | Drupal_NodeProductTemplate
        | Drupal_NodeWebinar
        | Drupal_NodeVideoLandingPage
        | Drupal_NodeCaseStudy;
      const nodeTids = [
        ...(fieldTopic || []).map(term => String(term!.targetId)),
        ...(fieldPersona || []).map(term => String(term!.targetId))
      ];

      return nodeTids.some(tid => tids.includes(tid));
    }
  };

  const matchesSearch = (entity: Drupal_Node | FormattedBlogPost) =>
    (entity.title || '').toLowerCase().includes(search.toLowerCase());

  const matchesPersona = (entity: Drupal_Node | FormattedBlogPost) => {
    if (!activePersona) {
      return true;
    }

    if (entity && entity.entityBundle === 'discovery') {
      const { fieldDiscoveryPersona } = entity as Drupal_NodeDiscovery;
      return (
        fieldDiscoveryPersona &&
        fieldDiscoveryPersona.some(
          persona => persona!.targetId === activePersona
        )
      );
    } else if (entity && entity.entityBundle === 'blog_post') {
      const { personaIds } = entity as FormattedBlogPost;
      return personaIds.some(persona => persona === activePersona.toString());
    }

    const { fieldPersona } = entity as
      | Drupal_NodeEbook
      | Drupal_NodeProductTemplate
      | Drupal_NodeWebinar
      | Drupal_NodeVideoLandingPage
      | Drupal_NodeCaseStudy;

    return (
      fieldPersona &&
      fieldPersona.some(persona => persona!.targetId === activePersona)
    );
  };

  const mapEntityToCard = (theme: cardTheme) => ({
    entityId,
    entityBundle,
    title,
    entityUrl,
    ...entity
  }: Drupal_Node | FormattedBlogPost) => {
    let description = '';
    let imgSrc = '';

    if (entityBundle === 'discovery') {
      const {
        fieldFeaturedImage,
        fieldTitleParagraph
      } = entity as Drupal_NodeDiscovery;
      description = getProcessedValue(fieldTitleParagraph);
      imgSrc = fieldFeaturedImage || '';
    }

    if (entityBundle === 'webinar') {
      const {
        fieldImageSrc,
        fieldWebinarDescription
      } = entity as Drupal_NodeWebinar;

      imgSrc = fieldImageSrc || '';
      description = getProcessedValue(fieldWebinarDescription);
    }

    if (entityBundle === 'ebook') {
      const { body, fieldImage } = entity as Drupal_NodeEbook;
      description = getProcessedValue(body);
      imgSrc =
        (fieldImage![0]!.entity as Drupal_ParagraphTemplateImage)
          .fieldImageUrl || '';
    }

    if (entityBundle === 'product_template') {
      const {
        fieldTemplateDescription,
        fieldImage
      } = entity as Drupal_NodeProductTemplate;
      description = getProcessedValue(fieldTemplateDescription);
      imgSrc =
        (fieldImage![0]!.entity as Drupal_ParagraphTemplateImage)
          .fieldImageUrl || '';
    }

    if (entityBundle === 'video_landing_page') {
      const {
        fieldSidebar,
        fieldFirstSectionVideoLink
      } = entity as Drupal_NodeVideoLandingPage;
      description = getProcessedValue(fieldSidebar);
      imgSrc = fieldFirstSectionVideoLink
        ? `https://i.ytimg.com/vi/${fieldFirstSectionVideoLink
          .split('/')
          .pop() as string}/sddefault.jpg`
        : '';
      entityBundle = 'Video';
    }

    if (entityBundle === 'case_study') {
      const { fieldHeroText, fieldHeroImage } = entity as Drupal_NodeCaseStudy;
      description = fieldHeroText || '';
      imgSrc = fieldHeroImage || '';
    }

    if (entityBundle === 'blog_post') {
      const { summary, image } = entity as FormattedBlogPost;
      description = summary || '';
      imgSrc = image || '';
    }

    const descriptionNoHtml =
      description.replace(/<(?:.|\n)*?>/gm, '').substr(0, 250) + '...';

    return (
      <Card
        key={entityId}
        description={descriptionNoHtml}
        header={title || ''}
        imgSrc={imgSrc}
        meta={startcase(entityBundle || '')}
        theme={theme}
        url={entityUrl ? entityUrl.path || '' : ''}
      />
    );
  };

  const heroCards = sortedEntities
    .filter(matchesPersona)
    .slice(0, 3)
    .map(mapEntityToCard(cardTheme.dark));

  const block = 'resource-center';
  let chosenTopics = chosenTopic === '' ? topics : topics[chosenTopic].children;

  if (Object.keys(chosenTopics).length === 0) {
    chosenTopics = { [chosenTopic]: topics[chosenTopic] };
  }

  const cardsByTid = new Map<string, JSX.Element[]>();

  Object.keys(chosenTopics).forEach(tid => {
    const topic = chosenTopics[tid];
    const childKeys = Object.keys(topic.children);
    cardsByTid.set(
      tid,
      sortedEntities
        .filter(matchesPersona)
        .filter(filterByTids([tid, ...childKeys]))
        .filter(matchesSearch)
        .map(mapEntityToCard(cardTheme.default))
    );
  });

  const tidHasResults = (tid: string) => (cardsByTid.get(tid) || []).length > 0;

  const topicOptions = new Map<string, ValueType<OptionType>>();
  topicOptions.set('', {
    value: '',
    label: i18n('mktg-resource-center-topic-default')
  } as any);
  Object.keys(topics).forEach(tid => {
    topicOptions.set(tid, { value: tid, label: topics[tid]!.name! } as any);
  });

  const updateSearch = (newSearch: string) => {
    setSearch(newSearch);
    inputRef.current.value = newSearch;
    if (newSearch !== '') {
      filterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const debounceSearch = debounce(newSearch => {
    updateSearch(newSearch);
  }, 1000);

  const submitSearch = (evt: React.FormEvent<HTMLFormElement>) => {
    const newSearch = (evt.target as any)[0].value;
    evt.preventDefault();
    debounceSearch.cancel();
    updateSearch(newSearch);
  };

  const hasNoResults = Array.from(cardsByTid.values()).every(
    arr => arr.length === 0
  );

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    const params = [];

    if (chosenTopic && topics[chosenTopic]) {
      const chosenTopicSafeName = kebabcase(topics[chosenTopic].name!);
      params.push(`topic=${chosenTopicSafeName}`);
    }
    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
      inputRef.current.value = search;
    }
    const paramString = params.length > 0 ? `?${params.join('&')}` : '?';

    navigate(paramString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, chosenTopic]);

  // This effect sets the topic when the browser back button is used
  useEffect(() => {
    if ((queryTopicName || '') !== (topics[chosenTopic] || {}).name || '') {
      setChosenTopic(topicNameToTid.get(String(queryTopicName)) || '');
    }
    if ((querySearch || '') !== search) {
      const decodedSearch = decodeURIComponent(String(querySearch || ''));
      setSearch(decodedSearch);
      inputRef.current.value = decodedSearch;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (chosenSubtopic) {
      const chosenRef =
        (topicRefs[chosenSubtopic] || {}).current || filterRef.current;

      chosenRef.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenSubtopic]);

  const navbarMenu = useMenuMainNavLinks(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  const getMarketoLink = useMarketoLink({
    i18nKey,
    langPrefix
  });

  const { url, copy } = getMarketoLink('1128')!;

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      <NavbarAbTest menu={navbarMenu} scrollDistance={1} />
      <main className={block}>
        <header className={`${block}__hero`}>
          {node.fieldImageSrc && (
            <div
              className={`${block}__hero-background`}
              style={{ backgroundImage: `url(${node.fieldImageSrc})` }}
            />
          )}
          <div className={`${block}__hero-wrapper lucid-container`}>
            {node.fieldRcPersona && (
              <Breadcrumb
                inverse
                className={`${block}__breadcrumb`}
                items={[
                  <Link
                    key="/resource-center"
                    inverse
                    className={`${block}__link`}
                    to="/resource-center"
                  >
                    {i18n('mktg-nav-resources')}
                  </Link>,
                  node.fieldRcPersona.entity!.name
                ]}
              />
            )}
            <div className={`${block}__hero-copy`}>
              <Heading
                className={`${block}__title`}
                color="white"
                variant="headlineS"
              >
                {node.fieldFirstHeader}
              </Heading>
              <p
                className={`${block}__subheader ${
                  node.fieldRcPersona ? `${block}__subheader--bright` : ''
                  }`}
              >
                {node.fieldHeroText}
              </p>
            </div>
            <form
              className={`${block}__hero-search-wrapper`}
              onSubmit={submitSearch}
            >
              <input
                className={`${block}__hero-search`}
                onChange={({ target }) => {
                  const value = target.value;
                  debounceSearch(value);
                }}
                type="search"
              />
            </form>
          </div>
          <div className="lucid-container">
            {!node.fieldRcPersona && (
              <Grid
                className={`${block}__personas`}
                gutter={gridGutter.large}
                mobileLayout={gridLayout.halves}
                tabletGutter={gridGutter.medium}
                vgutter={gridGutter.small}
              >
                {Object.keys(personas).map(tid => {
                  const persona: Drupal_TaxonomyTermResourceCenterPersonas =
                    personas[tid];
                  return persona.fieldResourceCenterNode! ? (
                    <Link
                      key={tid}
                      inverse
                      className={`${block}__persona-link`}
                      to={
                        persona.fieldResourceCenterNode!.entity!.entityUrl!
                          .path!
                      }
                    >
                      {persona.name}
                    </Link>
                  ) : null;
                })}
              </Grid>
            )}
            <Grid
              gutter={gridGutter.xlarge}
              layout={gridLayout.hubHero}
              tabletGutter={gridGutter.large}
            >
              {heroCards}
            </Grid>
          </div>
        </header>
        <section
          ref={filterRef}
          className={`${block}__filters lucid-container`}
        >
          <Grid
            className={`${block}__inputs`}
            gutter={gridGutter.xlarge}
            layout={gridLayout.halves}
            mobileLayout={gridLayout.whole}
          >
            <Select
              className={`${block}__topic-selector`}
              classNamePrefix={`${block}-topic-selector`}
              onChange={(selectedOption: ValueType<OptionType>) => {
                setChosenTopic((selectedOption as any).value);
              }}
              options={Array.from(topicOptions.values()) as any}
              value={topicOptions.get(chosenTopic)}
            />
            <form
              className={`${block}__search-wrapper`}
              onSubmit={submitSearch}
            >
              <input
                ref={inputRef}
                className={`${block}__search  ${block}__filter`}
                onChange={({ target }) => {
                  const value = target.value;
                  debounceSearch(value);
                }}
                type="search"
              />
            </form>
          </Grid>
          {Object.keys(chosenTopics).length > 1 && (
            <Grid mobileLayout={gridLayout.halves}>
              {Object.keys(chosenTopics)
                .filter(tidHasResults)
                .map(tid => {
                  const topic = chosenTopics[tid];

                  return (
                    <Button
                      key={tid}
                      asLink
                      className={`${block}__jumplink-button`}
                      onClick={() =>
                        topicRefs[tid].current.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        })}
                      underline="hover"
                    >
                      {topic.name}
                    </Button>
                  );
                })}
            </Grid>
          )}
        </section>
        {Object.keys(chosenTopics)
          .filter(tidHasResults)
          .map(tid => {
            const topic = chosenTopics[tid];
            const childKeys = Object.keys(topic.children);
            const cards = cardsByTid.get(tid) || [];

            return (
              <section
                key={topic.tid}
                ref={topicRefs[topic.tid!]}
                className="lucid-container"
              >
                <div className={`${block}__section`}>
                  <Heading as="h2" variant="subheadS">
                    {topic.name}
                  </Heading>
                  <div
                    className={`${block}__section-description`}
                    dangerouslySetInnerHTML={{
                      __html: getProcessedValue(topic.description)
                    }}
                  />
                  <Grid className={`${block}__subtopics`}>
                    {childKeys.map(childTid => (
                      <Button
                        key={childTid}
                        asLink
                        className={`${block}__subtopic`}
                        onClick={() => {
                          setChosenTopic(tid);
                          setChosenSubtopic(childTid);
                        }}
                        underline="hover"
                      >
                        {topic.children[childTid].name}
                      </Button>
                    ))}
                  </Grid>
                  <Paginator
                    items={cards}
                    nextClass={`${block}__view-more-wrapper`}
                    nextJsx={(
                      <span className={`${block}__view-more`}>
                        {i18n('mktg-v-resources-hub-view-more')}
                        <div className={`${block}__view-more-btn-icon`}>
                          <Icon type={IconType.CarotDown} />
                        </div>
                      </span>
                    )}
                    pageSize={4}
                    render={visibleItems => (
                      <Grid
                        gutter={gridGutter.xlarge}
                        layout={gridLayout.hub}
                        tabletGutter={gridGutter.large}
                      >
                        {visibleItems}
                      </Grid>
                    )}
                  />
                </div>
              </section>
            );
          })}
        {hasNoResults && (
          <p className={`${block}__no-results`}>
            {i18n('mktg-resource-center-no-results')}
          </p>
        )}
        <aside className={`${block}__subfooter`}>
          <div className={`lucid-container  ${block}__subfooter-wrapper`}>
            <Grid
              gutter={gridGutter.xlarge}
              layout={gridLayout.halves}
              mobileLayout={gridLayout.whole}
            >
              <div />
              <div className={`${block}__subfooter-content`}>
                <Heading as="h2" color="white" variant="subheadS">
                  {i18n('mktg-subfooter-header')}
                </Heading>
                <p className={`${block}__subfooter-blurb`}>
                  {i18n('mktg_subfooter_p')}
                </p>
                <div className={`${block}__subfooter-buttons`}>
                  <Link
                    asButton
                    primary
                    className={`${block}__subfooter-button  ${block}__subfooter-button--secondary`}
                    to={`${productUrl}/documents/editNewOrRegister/466e7eac-5163-05d7-b7f1-42440a00189f/`}
                  >
                    {i18n('mktg_subfooter_cta_secondary')}
                  </Link>
                  <Link
                    asButton
                    secondary
                    className={`${block}__subfooter-button`}
                    to={url}
                  >
                    {i18n('mktg_subfooter_cta_primary') || copy}
                  </Link>
                </div>
              </div>
            </Grid>
          </div>
        </aside>
      </main>
      <FooterAbTest entityTranslations={entityTranslations} menu={footerMenu} />
    </Layout>
  );
};

export default ResourceCenter;

export const query = graphql`
  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $langPrefix: [String!]
    $i18nKey: String!
  ) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeResourceCenter {
          ...entityFields
          fieldFirstHeader
          fieldHeroText
          fieldImageSrc
          fieldRcPersona {
            targetId
            entity {
              name
            }
          }
        }
      }

      nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            {
              field: "type"
              operator: IN
              value: [
                "discovery"
                "webinar"
                "ebook"
                "product_template"
                "video_landing_page"
                "case_study"
              ]
            }
            { field: "status", value: "1", operator: EQUAL }
            { field: "langcode", value: $langPrefix, operator: EQUAL }
          ]
          groups: [
            {
              conjunction: OR
              conditions: [
                { field: "field_is_private", value: "false", operator: EQUAL }
                { field: "field_is_private", operator: IS_NULL }
              ]
            }
            {
              conjunction: OR
              conditions: [
                { field: "field_persona", operator: IS_NOT_NULL }
                { field: "field_topic", operator: IS_NOT_NULL }
                { field: "field_discovery_persona", operator: IS_NOT_NULL }
                { field: "field_discovery_topics", operator: IS_NOT_NULL }
              ]
            }
          ]
        }
        sort: [
          { field: "sticky", direction: DESC }
          { field: "created", direction: DESC }
        ]
      ) {
        entities {
          entityTranslation(language: $LanguageId) {
            entityId
            entityBundle
            entityUrl {
              path
            }
            ... on Drupal_Node {
              title
              sticky
              created
            }
            ... on Drupal_NodeDiscovery {
              fieldTitleParagraph {
                processed
              }
              fieldFeaturedImage
              fieldDiscoveryPersona {
                targetId
              }
              fieldDiscoveryTopics {
                targetId
              }
            }
            ... on Drupal_NodeWebinar {
              fieldWebinarDescription {
                processed
              }
              fieldImageSrc
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeEbook {
              body {
                processed
              }
              fieldImage {
                entity {
                  ... on Drupal_ParagraphTemplateImage {
                    fieldImageUrl
                  }
                }
              }
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeProductTemplate {
              fieldTemplateDescription {
                processed
              }
              fieldImage {
                entity {
                  ... on Drupal_ParagraphTemplateImage {
                    fieldImageUrl
                  }
                }
              }
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeVideoLandingPage {
              fieldSidebar {
                processed
              }
              fieldFirstSectionVideoLink
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeCaseStudy {
              fieldHeroText
              fieldHeroImage
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
          }
        }
      }

      topics: taxonomyTermQuery(
        filter: {
          conditions: [
            { field: "vid", value: "resource_center_topics", operator: EQUAL }
          ]
        }
        limit: 10000
      ) {
        entities {
          ... on Drupal_TaxonomyTerm {
            name
            description {
              processed
            }
            tid
            parent {
              targetId
            }
          }
        }
      }

      personas: taxonomyTermQuery(
        filter: {
          conditions: [
            { field: "vid", value: "resource_center_personas", operator: EQUAL }
          ]
        }
        limit: 10000
      ) {
        entities {
          ... on Drupal_TaxonomyTerm {
            name
            tid
            parent {
              targetId
            }
            ... on Drupal_TaxonomyTermResourceCenterPersonas {
              fieldResourceCenterNode {
                entity {
                  entityUrl {
                    path
                  }
                }
              }
            }
          }
        }
      }
    }

    allBlogPosts(
      filter: {
        id: { ne: "dummy" }
        field_persona: { ne: "" }
        field_topic: { ne: "" }
      }
    ) {
      edges {
        node {
          nid
          title
          path
          created
          field_homepage_summary
          field_featured_image_url
          field_persona
          field_topic
        }
      }
    }

    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_subfooter_header
            mktg_subfooter_p
            mktg_subfooter_cta_primary
            mktg_subfooter_cta_secondary
            mktg_v_resources_hub_view_more
            mktg_resource_center_no_results
            mktg_resource_center_topic_default
            mktg_nav_resources
          }
        }
      }
    }
  }
`;
